import { useId } from "react";
import { useTranslation } from "react-i18next";

/**
 * apps/web :: components/admin :: ChoiceEditor
 *
 * Single row of the dynamic choices list inside
 * `<QuestionEditorForm>`. Callers supply the row's id + text, the
 * currently-selected `correctChoiceId`, and callbacks for text edits,
 * correct-radio flips, and row removal. The parent owns all state —
 * this stays a pure leaf so the exclusive-radio semantics are enforced
 * at the form level (one correct answer across all rows).
 */
export interface ChoiceEditorProps {
  index: number;
  choice: { id: string; text: string };
  /** Form-scoped radio group name. All rows share one name so the radios
   *  are mutually exclusive — same as native form-radio semantics. */
  radioGroupName: string;
  isCorrect: boolean;
  onTextChange: (index: number, text: string) => void;
  onSelectCorrect: (index: number) => void;
  onRemove: (index: number) => void;
  /** When true, the remove button is disabled (e.g. only 2 choices left). */
  canRemove: boolean;
  disabled?: boolean;
}

export default function ChoiceEditor({
  index,
  choice,
  radioGroupName,
  isCorrect,
  onTextChange,
  onSelectCorrect,
  onRemove,
  canRemove,
  disabled = false,
}: ChoiceEditorProps) {
  const { t } = useTranslation();
  const radioId = useId();
  const textId = useId();
  const label = choice.id || String.fromCharCode(97 + index); // 'a', 'b', ...

  return (
    <div
      data-testid={`admin-choice-row-${index}`}
      className="flex items-start gap-sm rounded-md border border-neutral-200 bg-white p-sm"
    >
      <div className="pt-xs">
        <input
          id={radioId}
          data-testid={`admin-choice-correct-${index}`}
          type="radio"
          name={radioGroupName}
          checked={isCorrect}
          disabled={disabled}
          onChange={() => onSelectCorrect(index)}
          className="h-4 w-4 cursor-pointer accent-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        />
        <label htmlFor={radioId} className="sr-only">
          {t("admin.editor.fields.correctChoice")} {label.toUpperCase()}
        </label>
      </div>
      <div className="flex-1">
        <label
          htmlFor={textId}
          className="block text-xs font-semibold uppercase tracking-wide text-neutral-600"
        >
          {label.toUpperCase()}
        </label>
        <input
          id={textId}
          data-testid={`admin-choice-text-${index}`}
          type="text"
          value={choice.text}
          onChange={(ev) => onTextChange(index, ev.currentTarget.value)}
          disabled={disabled}
          maxLength={500}
          className="mt-xs w-full rounded-md border border-neutral-300 bg-white px-sm py-xs text-sm text-neutral-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        />
      </div>
      <button
        type="button"
        data-testid={`admin-choice-remove-${index}`}
        disabled={disabled || !canRemove}
        onClick={() => onRemove(index)}
        aria-label={t("admin.editor.fields.removeChoice", {
          label: label.toUpperCase(),
        })}
        className="mt-md inline-flex items-center rounded-md border border-neutral-300 bg-white px-sm py-xs text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
      >
        ×
      </button>
    </div>
  );
}
